import { Spinner, Stack, Text, Heading } from '@chakra-ui/react'
import type { RegistrationStatus } from '@ipc/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { api, isApiError } from '~/api/client'
import { keys } from '~/api/hooks'
import { FlowStepper } from '~/components/member/FlowStepper'
import { MemberLayout } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { pathForStatus, registrationEndedPath } from '~/lib/flow'
import { useFlowSteps } from '~/lib/steps'

export const POLL_MS = 3000
export const SLOW_AFTER_MS = 2 * 60 * 1000

/**
 * S4-wait (every provider): polls the registration every 3 seconds until the check is no longer pending, then hands
 * the status on. After 2 minutes it says the page can be closed (an email follows). It never shows a score.
 */
export function Wait({
  onDecided,
  onLoggedOut,
  finishing,
  queryKey = keys.registration,
  heading = true,
}: {
  onDecided: (r: RegistrationStatus) => void
  onLoggedOut?: () => void
  /** The provider's name: shows "Please wait a moment" (E10.6) instead of "Checking your face...". */
  finishing?: string
  /** Own cache entry for a Wait inside another screen, so the page behind it does not move on its own. */
  queryKey?: readonly unknown[]
  /** Show the screen's own "Checking your face..." heading. The return page has its own h1, so it passes false. */
  heading?: boolean
}) {
  const { t } = useT()
  const { required } = useFlowSteps()
  const [slow, setSlow] = useState(false)
  const q = useQuery<RegistrationStatus>({
    queryKey,
    queryFn: () => api('registrationStatus'),
    refetchInterval: (query) => (query.state.data && query.state.data.stage !== 'biometric_pending' ? false : POLL_MS),
    retry: (count, err) => !(isApiError(err) && err.status < 500) && count < 2,
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    const id = setTimeout(() => setSlow(true), SLOW_AFTER_MS)
    return () => clearTimeout(id)
  }, [])

  const data = q.data
  const fired = useRef(false)
  useEffect(() => {
    // Only an answer fetched after this screen opened counts: the cache may hold the status from before the check.
    if (fired.current || !q.isFetchedAfterMount || !data || data.stage === 'biometric_pending') return
    fired.current = true
    onDecided(data)
  }, [data, q.isFetchedAfterMount, onDecided])
  useEffect(() => {
    if (q.isError && isApiError(q.error) && q.error.status === 401) onLoggedOut?.()
  }, [q.isError, q.error, onLoggedOut])

  return (
    <Stack gap={4} role='status' aria-live='polite'>
      {heading && (
        <Heading as='h2' size='xl'>
          {finishing ? t('embedded.oneMoment') : t('wait.title')}
        </Heading>
      )}
      <Spinner size='xl' color='brand.solid' borderWidth='3px' aria-hidden />
      <Text>{finishing ? t('embedded.finishing', { provider: finishing }) : t('wait.text')}</Text>
      {finishing && <Text>{t('embedded.finishingMail')}</Text>}
      {required && <Text>{t('wait.thenDocument')}</Text>}
      {slow && <Text fontWeight='bold'>{t('wait.slow')}</Text>}
    </Stack>
  )
}

/** `/join/:providerId/wait` (S4-wait). */
export default function WaitPage() {
  const navigate = useNavigate()
  const { providerId } = useParams()
  return (
    <MemberLayout flow>
      <FlowStepper step='face' art='wait' />
      <Wait
        onDecided={(r) => navigate(pathForStatus(r), { replace: true })}
        onLoggedOut={() => {
          const ended = registrationEndedPath(providerId)
          navigate(ended.to, { replace: true, state: ended.state })
        }}
      />
    </MemberLayout>
  )
}
